import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { exitAPI, employeesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, LogOut, AlertTriangle, CheckCircle2, XCircle, UserX, Undo2 } from 'lucide-react';

const TERM_REASONS = ['performance', 'misconduct', 'redundancy', 'probation_failure', 'absconding', 'fraud', 'other'];
const STATUS_COLORS = { pending: 'secondary', approved: 'default', rejected: 'destructive', withdrawn: 'outline' };

const ExitManagement = () => {
  const { user } = useAuth();
  const [resignations, setResignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisionDialog, setDecisionDialog] = useState(null); // {resignation, mode: 'approve'|'reject'}
  const [decForm, setDecForm] = useState({ last_working_day: '', comments: '' });
  const [terminateDialog, setTerminateDialog] = useState(null); // employee
  const [termForm, setTermForm] = useState({ last_working_day: '', reason: 'performance', notes: '', is_eligible_for_rehire: false });
  const [acting, setActing] = useState(false);

  const allowedRole = !user || ['super_admin', 'hr_admin', 'manager'].includes(user?.role);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([exitAPI.list(), employeesAPI.getAll()]);
      setResignations(r.data);
      setEmployees(e.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (allowedRole) fetchAll(); /* eslint-disable-next-line */ }, [allowedRole]);

  const openDecision = (resignation, mode) => {
    setDecisionDialog({ resignation, mode });
    setDecForm({ last_working_day: resignation.last_working_day, comments: '' });
  };
  const submitDecision = async () => {
    if (!decisionDialog) return;
    setActing(true);
    try {
      await exitAPI.decide(decisionDialog.resignation.id, {
        decision: decisionDialog.mode,
        last_working_day: decForm.last_working_day,
        comments: decForm.comments,
      });
      toast.success(`Resignation ${decisionDialog.mode}d`);
      setDecisionDialog(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  const openTerminate = (emp) => {
    setTerminateDialog(emp);
    setTermForm({ last_working_day: new Date().toISOString().split('T')[0], reason: 'performance', notes: '', is_eligible_for_rehire: false });
  };
  const submitTerminate = async () => {
    if (!terminateDialog) return;
    if (!termForm.last_working_day || !termForm.reason) { toast.error('Last working day and reason required'); return; }
    if (!window.confirm(`Terminate ${terminateDialog.first_name} ${terminateDialog.last_name}? Their account access will be revoked. This action is reversible only by reactivation.`)) return;
    setActing(true);
    try {
      await exitAPI.terminate(terminateDialog.id, termForm);
      toast.success('Employee terminated');
      setTerminateDialog(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Termination failed'); }
    finally { setActing(false); }
  };

  const reactivate = async (emp) => {
    if (!window.confirm(`Reactivate ${emp.first_name} ${emp.last_name}? Their status will be set back to active and login restored.`)) return;
    try {
      await exitAPI.reactivate(emp.id);
      toast.success('Reactivated');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!allowedRole) return <Navigate to="/" replace />;

  const pending = resignations.filter(r => r.status === 'pending');
  const decided = resignations.filter(r => r.status !== 'pending');
  const recentExits = employees.filter(e => ['resigned', 'terminated', 'contract_ended', 'retired', 'absconded'].includes(e.status));
  const activeEmployees = employees.filter(e => e.status === 'active');

  return (
    <div className="space-y-6" data-testid="exit-management-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><LogOut />Exit Management</h1>
        <p className="text-muted-foreground">Approve resignations, initiate terminations, and review recent exits</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Pending Resignations</p><p className="text-2xl font-bold text-amber-600">{pending.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Approved (history)</p><p className="text-2xl font-bold">{decided.filter(d => d.status === 'approved').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Recent Exits</p><p className="text-2xl font-bold text-red-600">{recentExits.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Active Headcount</p><p className="text-2xl font-bold text-emerald-600">{activeEmployees.length}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="resignations">
        <TabsList>
          <TabsTrigger value="resignations" data-testid="tab-resignations">Resignations ({pending.length})</TabsTrigger>
          <TabsTrigger value="terminate" data-testid="tab-terminate">Terminate Employee</TabsTrigger>
          <TabsTrigger value="exits" data-testid="tab-exits">Recent Exits ({recentExits.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="resignations">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Submitted</TableHead>
                <TableHead>Last Working Day</TableHead><TableHead>Reason</TableHead>
                <TableHead>Status</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {resignations.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No resignations</TableCell></TableRow>}
                {resignations.map(r => (
                  <TableRow key={r.id} data-testid={`resignation-row-${r.id}`}>
                    <TableCell className="font-medium">{r.employee_name}<div className="text-xs text-muted-foreground">{r.employee_code} · {r.designation}</div></TableCell>
                    <TableCell className="text-xs">{new Date(r.submitted_at).toLocaleDateString()}</TableCell>
                    <TableCell className="font-semibold">{r.last_working_day}</TableCell>
                    <TableCell className="text-sm">{r.reason}<div className="text-xs text-muted-foreground line-clamp-1">{r.feedback}</div></TableCell>
                    <TableCell><Badge variant={STATUS_COLORS[r.status]}>{r.status}</Badge></TableCell>
                    <TableCell>
                      {r.status === 'pending' ? (
                        <div className="flex gap-1">
                          <Button size="sm" variant="default" onClick={() => openDecision(r, 'approve')} data-testid={`approve-${r.id}`}><CheckCircle2 size={12} className="mr-1" />Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => openDecision(r, 'reject')} data-testid={`reject-${r.id}`}><XCircle size={12} className="mr-1" />Reject</Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">{r.decided_by ? `by ${r.decided_by}` : '—'}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="terminate">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Initiate Termination</CardTitle>
              <p className="text-xs text-muted-foreground">Use this for involuntary separation. Account access is revoked immediately.</p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Employee</TableHead><TableHead>Designation</TableHead>
                  <TableHead>Joining Date</TableHead><TableHead>Action</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {activeEmployees.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No active employees</TableCell></TableRow>}
                  {activeEmployees.slice(0, 50).map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.first_name} {e.last_name}<div className="text-xs text-muted-foreground">{e.employee_code} · {e.email}</div></TableCell>
                      <TableCell>{e.designation || '—'}<div className="text-xs text-muted-foreground">{e.department}</div></TableCell>
                      <TableCell className="text-xs">{e.joining_date || '—'}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="destructive" onClick={() => openTerminate(e)} data-testid={`terminate-${e.id}`}>
                          <UserX size={12} className="mr-1" />Terminate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {activeEmployees.length > 50 && <p className="text-xs text-muted-foreground p-3 text-center">Showing first 50 of {activeEmployees.length}. Use the Employees page for search.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="exits">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Exit Type</TableHead>
                <TableHead>Last Working Day</TableHead><TableHead>Reason</TableHead>
                <TableHead>Rehirable?</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {recentExits.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No exits recorded</TableCell></TableRow>}
                {recentExits.map(e => (
                  <TableRow key={e.id} data-testid={`exit-row-${e.id}`}>
                    <TableCell className="font-medium">{e.first_name} {e.last_name}<div className="text-xs text-muted-foreground">{e.employee_code} · {e.designation}</div></TableCell>
                    <TableCell><Badge variant={e.status === 'terminated' ? 'destructive' : 'secondary'}>{e.exit_type || e.status}</Badge></TableCell>
                    <TableCell>{e.last_working_day || '—'}</TableCell>
                    <TableCell className="text-sm">{e.exit_reason || '—'}<div className="text-xs text-muted-foreground line-clamp-1">{e.exit_notes}</div></TableCell>
                    <TableCell>{e.is_eligible_for_rehire ? <Badge>Yes</Badge> : <Badge variant="outline">—</Badge>}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => reactivate(e)} title="Reverse exit (mistake correction)" data-testid={`reactivate-${e.id}`}>
                        <Undo2 size={12} className="mr-1" />Reactivate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Resignation decision dialog */}
      <Dialog open={!!decisionDialog} onOpenChange={(o) => !o && setDecisionDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decisionDialog?.mode === 'approve' ? 'Approve' : 'Reject'} Resignation — {decisionDialog?.resignation.employee_name}</DialogTitle></DialogHeader>
          {decisionDialog && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Submitted on {new Date(decisionDialog.resignation.submitted_at).toLocaleDateString()}. Reason: <strong>{decisionDialog.resignation.reason}</strong></p>
              {decisionDialog.mode === 'approve' && (
                <div>
                  <Label>Confirmed Last Working Day *</Label>
                  <Input type="date" value={decForm.last_working_day} onChange={(e) => setDecForm({...decForm, last_working_day: e.target.value})} data-testid="dec-lwd" />
                  <p className="text-xs text-muted-foreground mt-1">Employee requested {decisionDialog.resignation.last_working_day}. You may adjust based on the notice period.</p>
                </div>
              )}
              <div>
                <Label>{decisionDialog.mode === 'reject' ? 'Reason for rejection' : 'Comments'} (optional)</Label>
                <Textarea value={decForm.comments} onChange={(e) => setDecForm({...decForm, comments: e.target.value})} rows={3} data-testid="dec-comments" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog(null)}>Cancel</Button>
            <Button variant={decisionDialog?.mode === 'approve' ? 'default' : 'destructive'} onClick={submitDecision} disabled={acting} data-testid="confirm-decision-btn">
              {acting && <Loader2 size={14} className="animate-spin mr-1" />}Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Termination dialog */}
      <Dialog open={!!terminateDialog} onOpenChange={(o) => !o && setTerminateDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Terminate {terminateDialog?.first_name} {terminateDialog?.last_name}</DialogTitle></DialogHeader>
          {terminateDialog && (
            <div className="space-y-3">
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded p-3 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <span>This will set the employee's status to <strong>terminated</strong>, set their last working day, and immediately deactivate their user account. They will be unable to log in.</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Last Working Day *</Label><Input type="date" value={termForm.last_working_day} onChange={(e) => setTermForm({...termForm, last_working_day: e.target.value})} data-testid="term-lwd" /></div>
                <div>
                  <Label>Reason *</Label>
                  <Select value={termForm.reason} onValueChange={(v) => setTermForm({...termForm, reason: v})}>
                    <SelectTrigger data-testid="term-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>{TERM_REASONS.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes (optional)</Label><Textarea value={termForm.notes} onChange={(e) => setTermForm({...termForm, notes: e.target.value})} rows={3} placeholder="Internal notes — visible only to HR" data-testid="term-notes" /></div>
              <div className="flex items-center gap-2">
                <Switch checked={termForm.is_eligible_for_rehire} onCheckedChange={(v) => setTermForm({...termForm, is_eligible_for_rehire: v})} data-testid="rehirable-switch" />
                <Label>Eligible for rehire</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTerminateDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitTerminate} disabled={acting} data-testid="confirm-terminate-btn">
              {acting && <Loader2 size={14} className="animate-spin mr-1" />}<UserX size={14} className="mr-1" />Confirm Termination
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExitManagement;
